/**
 * StructureModule — the Structures tab (P14.1).
 *
 * Surfaces the SWG deed-deployment loop on the shared Commons: the lot budget,
 * mintable deeds, the player's deployed structures with their maintenance pool +
 * condition, the maintenance top-up, and the two-step redeed. The client RENDERS
 * replicated rows + CALLS reducers; every validity / lot / maintenance / decay /
 * redeed decision is server-authoritative.
 *
 * The full green/red/yellow 3D placement map + the in-house decoration gizmo are
 * world-canvas features; this panel deploys to an auto-assigned Commons spot and
 * wires the authoritative actions. Mirrors the OnboardingModule shape.
 */

import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import type { Identity } from 'spacetimedb';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { STRUCTURE_TYPES } from '@shared/types/structures.js';
import { iconMarkup } from '../lib/icons.js';

const LOT_TOTAL_DEFAULT = 10;

export class StructureModule implements IModule {
  readonly name = 'Structures';
  private bound = false;
  private els: Record<string, HTMLElement> = {};
  /** structureId (hex string) whose redeed is armed (awaiting a confirm). */
  private armedRedeed: string | null = null;
  /** Trailing-debounce handle for the row-storm re-render ping (see bind). */
  private pingTimer: number | null = null;
  /** Auto-assigned deploy cell lane (the panel has no 3D placement map). */
  private cell = 400 + Math.floor(Math.random() * 200000);
  private readonly laneZ = 200 + Math.floor(Math.random() * 2000);

  init(_app: App): void {
    for (const id of ['st-budget', 'st-deeds', 'st-list', 'st-note']) {
      const el = document.getElementById(id);
      if (el) this.els[id] = el;
    }
    bus.on('atlas:account-ready', () => {
      this.bind();
      this.render();
    });
    for (const ev of ['atlas:structures-updated', 'atlas:wallet-updated', 'atlas:world-entered'] as const) {
      bus.on(ev, () => this.render());
    }
    this.render();
  }

  private bind(): void {
    if (this.bound) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.bound = true;
    // Trailing debounce: the initial sync replays every deed/structure row and
    // each ping used to rebuild all three panels' innerHTML per ROW — O(rows²)
    // DOM work at join (the placement-flood/tab-freeze lesson).
    const ping = (): void => {
      if (this.pingTimer !== null) return;
      this.pingTimer = window.setTimeout(() => {
        this.pingTimer = null;
        bus.emit('atlas:structures-updated', {});
      }, 150);
    };
    for (const tbl of [conn.db.lotBudget, conn.db.structureDeed, conn.db.structure, conn.db.structureItem]) {
      const t = tbl as unknown as { onInsert(cb: () => void): void; onUpdate(cb: () => void): void; onDelete(cb: () => void): void };
      t.onInsert(ping);
      t.onUpdate(ping);
      t.onDelete(ping);
    }
  }

  private render(): void {
    const conn = atlas.conn;
    if (!conn || !conn.identity) return;
    const me = conn.identity;
    this.renderBudget(conn, me);
    this.renderDeeds(conn, me);
    this.renderList(conn, me);
  }

  private nextSpot(): { x: number; z: number } {
    const x = this.cell * 64 + 32;
    this.cell += 7;
    return { x, z: this.laneZ * 64 + 32 };
  }

  private renderBudget(conn: DbConnection, me: Identity): void {
    const el = this.els['st-budget'];
    if (!el) return;
    const lb = conn.db.lotBudget.identity.find(me);
    const used = lb ? lb.lotsUsed : 0;
    const total = lb ? lb.lotsTotal : LOT_TOTAL_DEFAULT;
    const pct = total > 0 ? Math.round((used / total) * 100) : 0;
    el.innerHTML =
      `<div class="meta">${iconMarkup('home')}Lots: <b>${used}/${total}</b> used</div>` +
      `<div style="height:8px;background:var(--ax-bg3);border-radius:var(--ax-radius);overflow:hidden"><div style="height:100%;width:${pct}%;background:var(--ax-accent)"></div></div>`;
  }

  private renderDeeds(conn: DbConnection, me: Identity): void {
    const el = this.els['st-deeds'];
    if (!el) return;
    const opts = STRUCTURE_TYPES.map((t) => `<option value="${t}">${t.replace(/_/g, ' ')}</option>`).join('');
    const deeds = [...conn.db.structureDeed.iter()].filter((d) => d.owner.isEqual(me) && d.packed);
    const deedRows = deeds.length
      ? deeds
          .map(
            (d) =>
              `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('passport')}${this.esc(d.structureType.replace(/_/g, ' '))}${d.noTrade ? ' <span class="price">No-Trade</span>' : ''}</span><span class="price">${d.lotsRequired} lots</span></div>` +
              `<div class="meta">${d.baseMaintPerHour} SOV/h upkeep · item cap ${d.itemCap}${d.storedMaint > 0n ? ` · stored ${d.storedMaint} SOV` : ''}</div>` +
              `<button class="action" data-deploy="${d.deedId.toString()}" data-type="${this.esc(d.structureType)}">Deploy onto the Commons</button></div>`,
          )
          .join('')
      : '<div class="empty">No deeds. Mint one above to deploy onto the Commons.</div>';

    el.innerHTML =
      `<div class="row"><select id="st-deed-type" title="Deed type">${opts}</select><button class="action secondary" id="st-grant-btn">Get deed</button></div>` +
      deedRows;

    el.querySelector('#st-grant-btn')?.addEventListener('click', () => {
      const sel = el.querySelector('#st-deed-type') as HTMLSelectElement | null;
      this.grantDeed(sel?.value ?? 'small_house');
    });
    for (const btn of Array.from(el.querySelectorAll('[data-deploy]'))) {
      btn.addEventListener('click', () => this.deploy(BigInt((btn as HTMLElement).dataset.deploy!), (btn as HTMLElement).dataset.type ?? 'small_house'));
    }
  }

  private renderList(conn: DbConnection, me: Identity): void {
    const el = this.els['st-list'];
    if (!el) return;
    const mine = [...conn.db.structure.iter()].filter((s) => s.owner.isEqual(me)).sort((a, b) => (a.structureId > b.structureId ? -1 : 1));
    if (mine.length === 0) {
      el.innerHTML = '<div class="empty">No deployed structures yet.</div>';
      return;
    }
    el.innerHTML = mine
      .map((s) => {
        const sid = s.structureId.toString();
        const condPct = Math.round((s.condition / 1000) * 100);
        const armed = this.armedRedeed === sid;
        return (
          `<div class="market-item"><div class="head"><span class="kind">${iconMarkup('home')}${this.esc(s.structureType.replace(/_/g, ' '))} #${sid}</span><span class="price">pool ${s.maintPool} SOV</span></div>` +
          `<div class="meta">cell (${s.cellX}, ${s.cellZ}) · items ${s.itemCount}/${s.itemCap} · ${s.outsideCity ? '⚠ outside a city (3× upkeep)' : 'in a city'}</div>` +
          `<div class="meta">condition ${s.condition}/1000</div>` +
          `<div style="height:6px;background:var(--ax-bg3);border-radius:var(--ax-radius);overflow:hidden;margin:4px 0"><div style="height:100%;width:${condPct}%;background:${condPct > 33 ? 'var(--ax-good)' : 'var(--ax-accent)'}"></div></div>` +
          `<button class="action secondary" data-pay="${sid}">Top up 100 SOV</button> ` +
          `<button class="action" data-redeed="${sid}">${armed ? 'Confirm redeed (30s)' : 'Redeed'}</button></div>`
        );
      })
      .join('');

    for (const btn of Array.from(el.querySelectorAll('[data-pay]'))) {
      btn.addEventListener('click', () => this.pay(BigInt((btn as HTMLElement).dataset.pay!)));
    }
    for (const btn of Array.from(el.querySelectorAll('[data-redeed]'))) {
      btn.addEventListener('click', () => this.redeed(BigInt((btn as HTMLElement).dataset.redeed!)));
    }
  }

  // ─── Actions ─────────────────────────────────────────────────────────────────

  private grantDeed(structureType: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.note(`Minting a ${structureType.replace(/_/g, ' ')} deed…`, '');
    void conn.reducers
      .grantDeed({ structureType })
      .then(() => this.note('Deed minted.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private deploy(deedId: bigint, structureType: string): void {
    const conn = atlas.conn;
    if (!conn) return;
    // Standing in the Commons → INTERACTIVE placement (ghost footprint with a
    // green/red validity preview, R/wheel rotation, click to deploy). From any
    // other world the terrain isn't loaded, so fall back to the legacy
    // auto-assigned spot.
    if (atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId) {
      bus.emit('atlas:structure-place', { deedId: deedId.toString(), structureType });
      this.note('Placement mode: move the cursor, R rotates, click to deploy, Esc cancels.', '');
      return;
    }
    const { x, z } = this.nextSpot();
    this.note('Deploying onto the Commons (auto spot — enter the Commons to place by hand)…', '');
    void conn.reducers
      .deployStructure({ deedId, posX: x, posZ: z, yaw: 0 })
      .then(() => this.note('Structure deployed.', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private pay(structureId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    void conn.reducers
      .payMaintenance({ structureId, amount: 100n })
      .then(() => this.note('Maintenance topped up (+100 SOV).', 'ok'))
      .catch((e: Error) => this.note(e.message, 'err'));
  }

  private redeed(structureId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    const sid = structureId.toString();
    if (this.armedRedeed !== sid) {
      // Arm the two-step confirm.
      void conn.reducers
        .redeedStructure({ structureId, confirm: false })
        .then(() => {
          this.armedRedeed = sid;
          this.note('Redeed armed — click Confirm within 30s. This repacks the structure into a No-Trade deed.', '');
          this.render();
        })
        .catch((e: Error) => this.note(e.message, 'err'));
      return;
    }
    // Confirm.
    void conn.reducers
      .redeedStructure({ structureId, confirm: true })
      .then(() => {
        this.armedRedeed = null;
        this.note('Structure repacked into a No-Trade deed; lots freed.', 'ok');
      })
      .catch((e: Error) => {
        this.armedRedeed = null;
        this.note(e.message, 'err');
        this.render();
      });
  }

  private note(text: string, cls: '' | 'ok' | 'err'): void {
    const el = this.els['st-note'];
    if (!el) return;
    el.className = cls ? `note ${cls}` : 'note';
    el.textContent = text;
  }

  /** Escape user-controlled strings before interpolating into innerHTML (anti-XSS). */
  private esc(s: string): string {
    return s.replace(/[&<>"']/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;'));
  }
}
